import React, { useState, useEffect, useRef } from "react";
import { FiMoreVertical } from "react-icons/fi";
import "../../styles/ChatScreen.css";
import { useAuth } from "../../context/AuthContext";
import { db, storage } from "../../firebase/config";
import {
  collection,
  addDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
  deleteDoc,
  updateDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import VideoCall from "../Call/VideoCall";
import AudioCall from "../Call/AudioCall";
import { socket } from "../../socket/signalingClient";

const ChatScreen = ({ selectedChat }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [audioCallOpen, setAudioCallOpen] = useState(false);

  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const chatId =
    selectedChat && currentUser
      ? [currentUser.uid, selectedChat.uid].sort().join("_")
      : null;

  // -----------------------------------------
  // LISTEN FOR MESSAGES
  // -----------------------------------------
  useEffect(() => {
    if (!selectedChat || !currentUser) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, "messages"),
      where("chatId", "==", chatId),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(q, async (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMessages(arr);

      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

      // Update delivery/read status
      arr.forEach(async (msg) => {
        if (msg.receiverId === currentUser.uid && !msg.seen) {
          const msgRef = doc(db, "messages", msg.id);
          const chatRef = doc(db, "chats", chatId);

          if (selectedChat?.uid === msg.senderId) {
            await updateDoc(msgRef, {
              seen: true,
              status: "read",
              seenAt: serverTimestamp(),
            });
            await updateDoc(chatRef, {
              [`unreadCount.${currentUser.uid}`]: 0,
            });
          } else {
            if (msg.status !== "delivered") {
              await updateDoc(msgRef, { status: "delivered" });
            }
            const chatSnap = await getDoc(chatRef);
            if (chatSnap.exists()) {
              const chatData = chatSnap.data();
              const currentUnread = chatData?.unreadCount?.[currentUser.uid] || 0;
              await updateDoc(chatRef, {
                [`unreadCount.${currentUser.uid}`]: currentUnread + 1,
              });
            }
          }
        }
      });
    });

    return () => unsub();
  }, [selectedChat, currentUser, chatId]);

  // -----------------------------------------
  // SEND TEXT MESSAGE
  // -----------------------------------------
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selectedChat || !currentUser) return;

    const receiverId = selectedChat.uid;
    const chatRef = doc(db, "chats", chatId);
    const messageData = {
      text: text.trim(),
      uid: currentUser.uid,
      email: currentUser.email || "",
      chatId,
      senderId: currentUser.uid,
      receiverId,
      type: "text",
      seen: false,
      seenAt: null,
      createdAt: serverTimestamp(),
      status: "sent",
      replyTo: replyTo ? replyTo.text : null,
    };

    await addDoc(collection(db, "messages"), messageData);

    const chatSnap = await getDoc(chatRef);
    const prevUnread = chatSnap.exists()
      ? chatSnap.data()?.unreadCount?.[receiverId] || 0
      : 0;

    await setDoc(
      chatRef,
      {
        users: [currentUser.uid, receiverId],
        lastMessage: messageData.text,
        lastMessageTime: serverTimestamp(),
        unreadCount: {
          [receiverId]: prevUnread + 1,
          [currentUser.uid]: 0,
        },
      },
      { merge: true }
    );

    setText("");
    setReplyTo(null);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  // -----------------------------------------
  // FILE UPLOAD
  // -----------------------------------------
  const handleFileClick = () => fileInputRef.current.click();

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file || !selectedChat || !currentUser) return;

      const receiverId = selectedChat.uid;
      const filePath = `attachments/${currentUser.uid}/${Date.now()}-${file.name}`;
      const fileRef = ref(storage, filePath);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const chatRef = doc(db, "chats", chatId);

      await addDoc(collection(db, "messages"), {
        text: file.name,
        fileUrl: url,
        fileType: file.type || "file",
        uid: currentUser.uid,
        email: currentUser.email || "",
        chatId,
        senderId: currentUser.uid,
        receiverId,
        type: "file",
        seen: false,
        seenAt: null,
        createdAt: serverTimestamp(),
        status: "sent",
      });

      const chatSnap = await getDoc(chatRef);
      const prevUnread = chatSnap.exists()
        ? chatSnap.data()?.unreadCount?.[receiverId] || 0
        : 0;

      await setDoc(
        chatRef,
        {
          users: [currentUser.uid, receiverId],
          lastMessage: file.name,
          lastMessageTime: serverTimestamp(),
          unreadCount: {
            [receiverId]: prevUnread + 1,
            [currentUser.uid]: 0,
          },
        },
        { merge: true }
      );

      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      alert("Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  // -----------------------------------------
  // FORMAT TIMESTAMP
  // -----------------------------------------
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    if (timestamp?.toDate) {
      const d = timestamp.toDate();
      let hours = d.getHours();
      const min = d.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${min} ${ampm}`;
    }
    return "";
  };

  const renderStatus = (msg) => {
    if (msg.senderId !== currentUser.uid) return null;
    if (msg.status === "sent") return <span className="msg-status">✓</span>;
    if (msg.status === "delivered") return <span className="msg-status">✓✓</span>;
    if (msg.status === "read") return <span className="msg-status seen">✓✓</span>;
  };

  // -----------------------------------------
  // RENDER
  // -----------------------------------------
  return (
    <div className="chat-container">
      <div className="chat-body scroll-body">
        {messages.map((msg) => {
          const isUser = msg.uid === currentUser?.uid;
          const menuOpen = activeMenu === msg.id;

          return (
            <div key={msg.id} className={`chat-bubble ${isUser ? "sent" : "received"}`}>
              {msg.replyTo && <div className="reply-block">↩ {msg.replyTo}</div>}
              <div className="msg-header">
                <span className="msg-sender">{isUser ? "You" : selectedChat?.name}</span>
                <div className="bubble-menu">
                  <button
                    className="msg-menu-btn"
                    onClick={() => setActiveMenu(menuOpen ? null : msg.id)}
                  >
                    <FiMoreVertical />
                  </button>
                  {menuOpen && (
                    <ul className="msg-dropdown">
                      <li onClick={() => setReplyTo(msg)}>Reply</li>
                      <li onClick={async () => await deleteDoc(doc(db, "messages", msg.id))}>
                        Delete
                      </li>
                    </ul>
                  )}
                </div>
              </div>
              <div className="msg-text">
                {msg.fileUrl ? (
                  <a href={msg.fileUrl} target="_blank" rel="noreferrer">
                    📎 {msg.text}
                  </a>
                ) : (
                  msg.text
                )}
              </div>
              <div className="msg-meta">
                <span className="msg-time">{formatTime(msg.createdAt)}</span>
                {renderStatus(msg)}
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      <div className="chat-footer-fixed">
        {showEmoji && (
          <div className="emoji-panel">
            {["😀","😂","😍","😭","😡","👍","❤️","🔥"].map((em) => (
              <span
                key={em}
                onClick={() => { setText((p) => p + em); setShowEmoji(false); }}
              >
                {em}
              </span>
            ))}
          </div>
        )}
        <form className="chat-input" onSubmit={sendMessage}>
          <button type="button" className="input-icon" onClick={() => setShowEmoji(!showEmoji)}>😊</button>
          <button type="button" className="input-icon" onClick={handleFileClick}>📎</button>
          <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
          <input
            type="text"
            placeholder={replyTo ? `Replying: ${replyTo.text}` : "Type a message..."}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit" className="send-btn">➤</button>
          {selectedChat && (
            <>
              <button type="button" className="call-btn" onClick={() => setCallOpen(true)}>📹 Call</button>
              <button type="button" className="call-btn" onClick={() => setAudioCallOpen(true)}>🎤 Audio</button>
            </>
          )}
        </form>
      </div>

      {callOpen && selectedChat && <VideoCall targetUser={selectedChat.uid} onClose={() => setCallOpen(false)} />}
      {audioCallOpen && selectedChat && <AudioCall targetUser={selectedChat.uid} onClose={() => setAudioCallOpen(false)} />}
    </div>
  );
};

export default ChatScreen;
